/**
 * AdminCapacitaciones.jsx - Gestión completa de capacitaciones
 * Permite ver, crear y editar categorías, módulos y evaluaciones
 */

import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Badge, Modal, Form, Alert, Spinner,
  Accordion, Row, Col, Tabs, Tab, ListGroup
} from 'react-bootstrap';
import { 
  FaGraduationCap, FaPlus, FaEdit, FaTrash, FaVideo, 
  FaFilePdf, FaClipboardCheck, FaToggleOn, FaToggleOff, FaYoutube,
  FaQuestionCircle, FaCheckCircle, FaTimesCircle
} from 'react-icons/fa';
import axios from 'axios';
import './AdminCapacitaciones.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4007').replace(/\/$/, '');

const ROLES_MAP = {
  0: 'Todos',
  1: 'Administración',
  2: 'Inspección',
  3: 'Taller',
  4: 'Conducción'
};

const AdminCapacitaciones = () => {
  const [categorias, setCategorias] = useState([]);
  const [modulosPorCategoria, setModulosPorCategoria] = useState({});
  const [evaluacionesPorModulo, setEvaluacionesPorModulo] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('categorias');
  
  // Modal categoría
  const [showModalCategoria, setShowModalCategoria] = useState(false);
  const [categoriaEditando, setCategoriaEditando] = useState(null);
  
  // Modal módulo
  const [showModalModulo, setShowModalModulo] = useState(false);
  const [moduloEditando, setModuloEditando] = useState(null);
  
  // Modal evaluación
  const [showModalEvaluacion, setShowModalEvaluacion] = useState(false);
  const [moduloParaEvaluacion, setModuloParaEvaluacion] = useState(null);
  
  // Categoría expandida
  const [categoriaExpandida, setCategoriaExpandida] = useState(null);

  useEffect(() => {
    cargarCategorias();
  }, []);

  const cargarCategorias = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/capacitaciones/categorias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCategorias(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarModulos = async (categoriaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/capacitaciones/modulos/${categoriaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setModulosPorCategoria(prev => ({
          ...prev,
          [categoriaId]: response.data.data
        }));
        
        // Cargar evaluaciones para cada módulo
        for (const modulo of response.data.data) {
          cargarEvaluacion(modulo.id);
        }
      }
    } catch (error) {
      console.error('Error al cargar módulos:', error);
    }
  };

  const cargarEvaluacion = async (moduloId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/capacitaciones/evaluacion/${moduloId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setEvaluacionesPorModulo(prev => ({
          ...prev,
          [moduloId]: response.data.data
        }));
      }
    } catch (error) {
      console.error('Error al cargar evaluación:', error);
    }
  };

  const handleExpandirCategoria = (categoriaId) => {
    if (categoriaExpandida === categoriaId) {
      setCategoriaExpandida(null);
    } else {
      setCategoriaExpandida(categoriaId);
      if (!modulosPorCategoria[categoriaId]) {
        cargarModulos(categoriaId);
      }
    }
  };

  const handleToggleActivoCategoria = async (categoria) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/capacitaciones/categorias/${categoria.id}/toggle-activo`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      cargarCategorias();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
    }
  };

  const renderIconoContenido = (modulo) => {
    if (modulo.tipo_contenido === 'youtube') {
      return <FaYoutube className="text-danger me-2" />;
    } else if (modulo.tipo_contenido === 'pdf') {
      return <FaFilePdf className="text-danger me-2" />;
    } else {
      return <FaVideo className="text-primary me-2" />;
    }
  };

  const renderTablaEvaluacion = (moduloId) => {
    const preguntas = evaluacionesPorModulo[moduloId] || [];
    
    if (preguntas.length === 0) {
      return (
        <Alert variant="info" className="mt-2">
          <FaQuestionCircle className="me-2" />
          Este módulo no tiene evaluación configurada
        </Alert>
      );
    }

    return (
      <div className="mt-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6>
            <FaClipboardCheck className="me-2 text-success" />
            Evaluación ({preguntas.length} preguntas)
          </h6>
          <Button 
            size="sm" 
            variant="outline-primary"
            onClick={() => {
              setModuloParaEvaluacion(moduloId);
              setShowModalEvaluacion(true);
            }}
          >
            <FaEdit className="me-1" /> Editar Evaluación
          </Button>
        </div>
        
        <Accordion>
          {preguntas.map((pregunta, idx) => (
            <Accordion.Item key={pregunta.id} eventKey={idx.toString()}>
              <Accordion.Header>
                <span className="fw-bold me-2">Pregunta {idx + 1}:</span>
                <span className="text-truncate">{pregunta.pregunta}</span>
              </Accordion.Header>
              <Accordion.Body>
                <div className="mb-2">
                  <strong>Pregunta:</strong> {pregunta.pregunta}
                </div>
                <div>
                  <strong>Opciones:</strong>
                  <ListGroup className="mt-2">
                    {['a', 'b', 'c', 'd'].map(opcion => {
                      const textoOpcion = pregunta[`opcion_${opcion}`];
                      const esCorrecta = pregunta.correcta === opcion;
                      
                      if (!textoOpcion) return null;
                      
                      return (
                        <ListGroup.Item 
                          key={opcion}
                          variant={esCorrecta ? 'success' : ''}
                          className="d-flex align-items-center"
                        >
                          {esCorrecta ? (
                            <FaCheckCircle className="text-success me-2" />
                          ) : (
                            <FaTimesCircle className="text-muted me-2" />
                          )}
                          <strong className="me-2">{opcion.toUpperCase()})</strong>
                          {textoOpcion}
                          {esCorrecta && (
                            <Badge bg="success" className="ms-auto">
                              Respuesta Correcta
                            </Badge>
                          )}
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    );
  };

  const renderCategoriasTab = () => (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <FaGraduationCap className="me-2" />
          Categorías de Capacitación
        </h5>
        <Button 
          variant="success" 
          size="sm"
          onClick={() => {
            setCategoriaEditando(null);
            setShowModalCategoria(true);
          }}
        >
          <FaPlus className="me-1" /> Nueva Categoría
        </Button>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Cargando categorías...</p>
          </div>
        ) : categorias.length === 0 ? (
          <Alert variant="info">No hay categorías creadas aún</Alert>
        ) : (
          categorias.map(categoria => (
            <Card key={categoria.id} className="mb-3 shadow-sm">
              <Card.Header 
                className="d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => handleExpandirCategoria(categoria.id)}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  <h6 className="mb-1">
                    {categoria.nombre}
                    <Badge 
                      bg={categoria.activo ? 'success' : 'secondary'} 
                      className="ms-2"
                    >
                      {categoria.activo ? 'Activa' : 'Inactiva'}
</Badge>
                    <Badge bg="info" className="ms-2">
                      {ROLES_MAP[categoria.rol_destinado]}
                    </Badge>
                  </h6>
                  <small className="text-muted">{categoria.descripcion}</small>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant={categoria.activo ? 'warning' : 'success'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleActivoCategoria(categoria);
                    }}
                  >
                    {categoria.activo ? <FaToggleOff /> : <FaToggleOn />}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCategoriaEditando(categoria);
                      setShowModalCategoria(true);
                    }}
                  >
                    <FaEdit />
                  </Button>
                </div>
              </Card.Header>
              
              {categoriaExpandida === categoria.id && (
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6>
                      Módulos ({modulosPorCategoria[categoria.id]?.length || 0})
                    </h6>
                    <Button
                      size="sm"
                      variant="outline-success"
                      onClick={() => {
                        setModuloEditando({ categoria_id: categoria.id });
                        setShowModalModulo(true);
                      }}
                    >
                      <FaPlus className="me-1" /> Nuevo Módulo
                    </Button>
                  </div>
                  
                  {!modulosPorCategoria[categoria.id] ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : modulosPorCategoria[categoria.id].length === 0 ? (
                    <Alert variant="warning">
                      Esta categoría no tiene módulos aún
                    </Alert>
                  ) : (
                    modulosPorCategoria[categoria.id].map(modulo => (
                      <Card key={modulo.id} className="mb-2 border">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <h6>
                                {renderIconoContenido(modulo)}
                                {modulo.titulo}
                                <Badge bg="secondary" className="ms-2">
                                  Módulo {modulo.orden}
                                </Badge>
                                {modulo.requiere_aprobacion === 1 && (
                                  <Badge bg="warning" className="ms-2">
                                    Requiere Aprobación
                                  </Badge>
                                )}
                              </h6>
                              
                              {modulo.video_url && (
                                <div className="mb-2">
                                  <small className="text-muted">
                                    <strong>Contenido:</strong> {modulo.video_url}
                                  </small>
                                </div>
                              )}
                              
                              {renderTablaEvaluacion(modulo.id)}
                            </div>
                            
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => {
                                  setModuloEditando(modulo);
                                  setShowModalModulo(true);
                                }}
                              >
                                <FaEdit />
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    ))
                  )}
                </Card.Body>
              )}
            </Card>
          ))
        )}
      </Card.Body>
    </Card>
  );

  return (
    <div className="admin-capacitaciones-container p-3">
      <Card className="mb-3">
        <Card.Header>
          <h4>
            <FaGraduationCap className="me-2" />
            Administración de Capacitaciones
          </h4>
          <p className="mb-0 text-muted">
            Gestiona categorías, módulos y evaluaciones de las capacitaciones
          </p>
        </Card.Header>
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="categorias" title={<><FaGraduationCap className="me-2" />Categorías y Módulos</>}>
          {renderCategoriasTab()}
        </Tab>
      </Tabs>

      {/* Modal Categoría */}
      <ModalCategoria
        show={showModalCategoria}
        onHide={() => setShowModalCategoria(false)}
        categoria={categoriaEditando}
        onSave={() => {
          setShowModalCategoria(false);
          cargarCategorias();
        }}
      />

      {/* Modal Módulo */}
      <ModalModulo
        show={showModalModulo}
        onHide={() => setShowModalModulo(false)}
        modulo={moduloEditando}
        categorias={categorias}
        onSave={() => {
          setShowModalModulo(false);
          if (moduloEditando?.categoria_id) {
            cargarModulos(moduloEditando.categoria_id);
          }
        }}
      />

      {/* Modal Evaluación */}
      <ModalEvaluacion
        show={showModalEvaluacion}
        onHide={() => setShowModalEvaluacion(false)}
        moduloId={moduloParaEvaluacion}
        preguntasExistentes={evaluacionesPorModulo[moduloParaEvaluacion] || []}
        onSave={() => {
          setShowModalEvaluacion(false);
          if (moduloParaEvaluacion) {
            cargarEvaluacion(moduloParaEvaluacion);
          }
        }}
      />
    </div>
  );
};

// Componente Modal para Categoría
const ModalCategoria = ({ show, onHide, categoria, onSave }) => {
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    activo: true,
    rol_destinado: '0'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (categoria) {
      setForm({
        nombre: categoria.nombre || '',
        descripcion: categoria.descripcion || '',
        activo: categoria.activo === 1,
        rol_destinado: String(categoria.rol_destinado || '0')
      });
    } else {
      setForm({
        nombre: '',
        descripcion: '',
        activo: true,
        rol_destinado: '0'
      });
    }
  }, [categoria, show]);

  const handleSubmit = async () => {
    if (!form.nombre.trim()) {
      alert('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (categoria) {
        // Actualizar (si hubiera endpoint PUT)
        alert('Funcionalidad de edición pendiente en backend');
      } else {
        // Crear nuevo
        await axios.post(
          `${API_URL}/api/capacitaciones/categorias`,
          form,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      
      onSave();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar la categoría');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {categoria ? 'Editar Categoría' : 'Nueva Categoría'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Nombre *</Form.Label>
            <Form.Control
              type="text"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Ej: Seguridad e Higiene"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="Descripción de la categoría"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Rol Destinatario</Form.Label>
            <Form.Select
              value={form.rol_destinado}
              onChange={(e) => setForm({ ...form, rol_destinado: e.target.value })}
            >
              {Object.entries(ROLES_MAP).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Activa"
              checked={form.activo}
              onChange={(e) => setForm({ ...form, activo: e.target.checked })}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? <Spinner animation="border" size="sm" /> : 'Guardar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Componente Modal para Módulo
const ModalModulo = ({ show, onHide, modulo, categorias, onSave }) => {
  const [form, setForm] = useState({
    categoria_id: '',
    titulo: '',
    orden: 1,
    video_url: '',
    tipo_contenido: 'youtube',
    requiere_aprobacion: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (modulo) {
      setForm({
        categoria_id: modulo.categoria_id || '',
        titulo: modulo.titulo || '',
        orden: modulo.orden || 1,
        video_url: modulo.video_url || '',
        tipo_contenido: modulo.tipo_contenido || 'youtube',
        requiere_aprobacion: modulo.requiere_aprobacion === 1
      });
    }
  }, [modulo, show]);

  const handleSubmit = async () => {
    if (!form.titulo.trim() || !form.categoria_id) {
      alert('El título y la categoría son requeridos');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const usuario = JSON.parse(localStorage.getItem('user'));
      
      const dataToSend = {
        ...form,
        subido_por: usuario?.Legajo || usuario?.CdEmpleado
      };

      if (modulo && modulo.id) {
        // Actualizar
        await axios.put(
          `${API_URL}/api/capacitaciones/modulos/${modulo.id}`,
          dataToSend,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        // Crear nuevo
        await axios.post(
          `${API_URL}/api/capacitaciones/modulos`,
          dataToSend,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      
      onSave();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar el módulo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {modulo?.id ? 'Editar Módulo' : 'Nuevo Módulo'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Categoría *</Form.Label>
            <Form.Select
              value={form.categoria_id}
              onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}
              disabled={modulo?.id} // No permitir cambiar categoría en edición
            >
              <option value="">Seleccionar categoría</option>
              {categorias.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.nombre}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Título del Módulo *</Form.Label>
            <Form.Control
              type="text"
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="Ej: Introducción a la seguridad"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Orden</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={form.orden}
              onChange={(e) => setForm({ ...form, orden: parseInt(e.target.value) })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tipo de Contenido</Form.Label>
            <Form.Select
              value={form.tipo_contenido}
              onChange={(e) => setForm({ ...form, tipo_contenido: e.target.value })}
            >
              <option value="youtube">YouTube</option>
              <option value="video">Video (archivo)</option>
              <option value="pdf">PDF</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>URL/Ruta del Contenido</Form.Label>
            <Form.Control
              type="text"
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder={
                form.tipo_contenido === 'youtube' 
                  ? 'https://www.youtube.com/embed/...'
                  : '/assets/Capacitaciones/...'
              }
            />
            <Form.Text className="text-muted">
              {form.tipo_contenido === 'youtube' 
                ? 'Utiliza el formato embed de YouTube'
                : 'Ruta relativa o absoluta del archivo'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Requiere Aprobación de Evaluación"
              checked={form.requiere_aprobacion}
              onChange={(e) => setForm({ ...form, requiere_aprobacion: e.target.checked })}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? <Spinner animation="border" size="sm" /> : 'Guardar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Componente Modal para Evaluación
const ModalEvaluacion = ({ show, onHide, moduloId, preguntasExistentes, onSave }) => {
  const [preguntas, setPreguntas] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (preguntasExistentes && preguntasExistentes.length > 0) {
      setPreguntas(preguntasExistentes.map(p => ({
        tipo: p.tipo || 'multiple',
        pregunta: p.pregunta || '',
        opcion_a: p.opcion_a || '',
        opcion_b: p.opcion_b || '',
        opcion_c: p.opcion_c || '',
        opcion_d: p.opcion_d || '',
        correcta: p.correcta || 'a'
      })));
    } else {
      setPreguntas([crearPreguntaVacia()]);
    }
  }, [preguntasExistentes, show]);

  const crearPreguntaVacia = () => ({
    tipo: 'multiple',
    pregunta: '',
    opcion_a: '',
    opcion_b: '',
    opcion_c: '',
    opcion_d: '',
    correcta: 'a'
  });

  const agregarPregunta = () => {
    setPreguntas([...preguntas, crearPreguntaVacia()]);
  };

  const eliminarPregunta = (index) => {
    setPreguntas(preguntas.filter((_, i) => i !== index));
  };

  const actualizarPregunta = (index, campo, valor) => {
    const nuevasPreguntas = [...preguntas];
    nuevasPreguntas[index][campo] = valor;
    setPreguntas(nuevasPreguntas);
  };

  const handleSubmit = async () => {
    // Validar que todas las preguntas tengan contenido
    const preguntasValidas = preguntas.every(p => 
      p.pregunta.trim() && p.opcion_a.trim() && p.opcion_b.trim()
    );

    if (!preguntasValidas) {
      alert('Todas las preguntas deben tener al menos la pregunta y dos opciones');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/capacitaciones/evaluacion/cargar`,
        {
          modulo_id: moduloId,
          preguntas: preguntas
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      onSave();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar la evaluación');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaClipboardCheck className="me-2" />
          Gestionar Evaluación del Módulo
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {preguntas.map((pregunta, index) => (
          <Card key={index} className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Pregunta {index + 1}</strong>
              {preguntas.length > 1 && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => eliminarPregunta(index)}
                >
                  <FaTrash />
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Pregunta *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={pregunta.pregunta}
                  onChange={(e) => actualizarPregunta(index, 'pregunta', e.target.value)}
                  placeholder="Escribe aquí la pregunta"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Opción A *</Form.Label>
                    <Form.Control
                      type="text"
                      value={pregunta.opcion_a}
                      onChange={(e) => actualizarPregunta(index, 'opcion_a', e.target.value)}
                      placeholder="Primera opción"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Opción B *</Form.Label>
                    <Form.Control
                      type="text"
                      value={pregunta.opcion_b}
                      onChange={(e) => actualizarPregunta(index, 'opcion_b', e.target.value)}
                      placeholder="Segunda opción"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Opción C</Form.Label>
                    <Form.Control
                      type="text"
                      value={pregunta.opcion_c}
                      onChange={(e) => actualizarPregunta(index, 'opcion_c', e.target.value)}
                      placeholder="Tercera opción (opcional)"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Opción D</Form.Label>
                    <Form.Control
                      type="text"
                      value={pregunta.opcion_d}
                      onChange={(e) => actualizarPregunta(index, 'opcion_d', e.target.value)}
                      placeholder="Cuarta opción (opcional)"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Respuesta Correcta *</Form.Label>
                <Form.Select
                  value={pregunta.correcta}
                  onChange={(e) => actualizarPregunta(index, 'correcta', e.target.value)}
                >
                  <option value="a">A - {pregunta.opcion_a || '(sin especificar)'}</option>
                  <option value="b">B - {pregunta.opcion_b || '(sin especificar)'}</option>
                  <option value="c">C - {pregunta.opcion_c || '(sin especificar)'}</option>
                  <option value="d">D - {pregunta.opcion_d || '(sin especificar)'}</option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        ))}

        <div className="text-center">
          <Button variant="outline-success" onClick={agregarPregunta}>
            <FaPlus className="me-2" />
            Agregar Otra Pregunta
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          disabled={saving || preguntas.length === 0}
        >
          {saving ? <Spinner animation="border" size="sm" /> : 'Guardar Evaluación'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdminCapacitaciones;
